// What the caller is told across the organization: every kind of notification they may be told of
// somewhere in it, each channel's value for the whole organization and where that value came from,
// and the choices they narrowed to one workspace or to one of the organization's channels. Read and
// changed by any member, for themselves.
//
// The vocabulary — a kind, a channel, where a value came from — is the registry's, published by the
// contract; the notification resources share it and the body their writes take.

import { api } from '../client'
import type { components } from '../schema'

export type NotificationKind = components['schemas']['NotificationKind']
export type NotificationChannel = components['schemas']['NotificationChannel']
export type NotificationSource = components['schemas']['NotificationSource']
export type NotificationScope = components['schemas']['NotificationScope']
export type NotificationChannelPreference = components['schemas']['NotificationChannelPreference']
export type NotificationWorkspaceChoice = components['schemas']['NotificationWorkspaceChoice']
export type NotificationWebsiteChoice = components['schemas']['NotificationWebsiteChoice']
export type NotificationKindPreference = components['schemas']['NotificationKindPreference']
export type NotificationPreferences = components['schemas']['NotificationPreferencesView']
export type NotificationChoiceChange = components['schemas']['NotificationChoiceChange']
export type UpdateNotificationChoices = components['schemas']['UpdateNotificationChoicesRequest']

const PREFERENCES = '/orgs/current/notification-preferences'

/**
 * The caller's notifications in the organization, in the registry's order. A kind is listed only
 * where the caller may be told of it somewhere — a workspace they read for an upload, the review
 * key for a membership request, a channel whose sales they run for a sale — so a member who may be
 * told of nothing gets an empty list.
 */
export function readNotificationPreferences(signal?: AbortSignal): Promise<NotificationPreferences> {
  return api.get<NotificationPreferences>(PREFERENCES, undefined, signal)
}

/**
 * Set (`true`, `false`) or clear (`null`, back to the organization's default or the platform's) the
 * caller's organization-wide choices, all or none; the answer is the view after the change. 400 for
 * a channel that is not `selectable` yet or a kind and channel named twice, 403 for a kind the
 * caller may not be told of, 422 for mail turned off on a mandatory kind.
 */
export function updateNotificationPreferences(body: UpdateNotificationChoices): Promise<NotificationPreferences> {
  return api.patch<NotificationPreferences>(PREFERENCES, body)
}
